/**
 * Spacing, radii, elevation, sizing and breakpoints.
 *
 * One 4px base grid shared by the phone app, the web build and the admin
 * portal, so a "card gap" is the same distance everywhere.
 */

/** 4px base grid. Never hardcode a margin — reach for a step. */
export const space = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 32,
  '4xl': 40,
  '5xl': 48,
  '6xl': 64,
} as const;

export type SpaceToken = keyof typeof space;

export const radius = {
  none: 0,
  sm: 6,
  md: 10,
  /** Cards, inputs, meal tiles — the dominant radius in the artwork. */
  lg: 14,
  xl: 20,
  /** Pills, status badges, filter chips. */
  full: 999,
} as const;

/**
 * Elevation. React Native and CSS express shadows differently, so each level
 * carries both. `elevation` drives Android, `shadow*` drives iOS, `css` drives
 * web and admin.
 */
export interface Elevation {
  shadowColor: string;
  shadowOpacity: number;
  shadowRadius: number;
  shadowOffset: { width: number; height: number };
  elevation: number;
  css: string;
}

const level = (
  y: number,
  blur: number,
  opacity: number,
  androidElevation: number,
): Elevation => ({
  shadowColor: '#16130F',
  shadowOpacity: opacity,
  shadowRadius: blur,
  shadowOffset: { width: 0, height: y },
  elevation: androidElevation,
  css: `0px ${y}px ${blur}px rgba(22, 19, 15, ${opacity})`,
});

export const elevation = {
  none: {
    shadowColor: 'transparent',
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
    elevation: 0,
    css: 'none',
  } as Elevation,
  /** Resting cards. The artwork uses very soft shadows on a cream ground. */
  card: level(1, 3, 0.06, 1),
  /** Raised surfaces: pinned summary bar, sticky CTA. */
  raised: level(2, 8, 0.08, 3),
  /** Drawer, modals, dropdowns. */
  overlay: level(8, 24, 0.16, 12),
} as const;

export type ElevationToken = keyof typeof elevation;

/** Fixed component sizing that several packages need to agree on. */
export const sizing = {
  /** Compact phone header, per the master prompt's phone rules. */
  headerHeight: 56,
  /** Minimum touch target. Enforced on every pressable. */
  touchTarget: 44,
  /** Icon ramp. */
  icon: { sm: 16, md: 20, lg: 24, xl: 32 },
  /** Drawer width as a fraction of screen width, capped in absolute terms. */
  drawerWidthRatio: 0.78,
  drawerMaxWidth: 340,
  /** Meal card in the horizontal carousel. */
  mealCardWidth: 240,
  mealCardImageHeight: 150,
  /** Vertical meal row: thumbnail edge and the row's resting height. */
  mealRowThumb: 84,
  mealRowHeight: 100,
  /** Splash lockup: the mark, and the rule and ornament beneath the wordmark. */
  /** The splash logo's widest; on a phone it takes 72% of the width. */
  splash: { logo: 300, rule: 64, ornament: 10 },
  /** Avatar sizes. */
  avatar: { sm: 32, md: 44, lg: 64 },
  /** Admin sidebar. */
  adminSidebarWidth: 240,
  /**
   * The donor app's column on a wide screen.
   *
   * It is a phone layout; letting it fill a 1600px browser stretches every
   * button across the page and leaves the eye travelling half a metre between
   * a label and its value. Centring it at phone-ish width keeps the approved
   * design intact on desktop instead of inventing a second one.
   */
  donorMaxWidth: 560,
  /** Max readable width for centred desktop content. */
  contentMaxWidth: 1200,
  /** Form column width on desktop checkout. */
  formColumnMaxWidth: 520,
  /**
   * Below this window height the donor screens stop pinning chrome.
   *
   * A phone on its side is about 360-410pt tall. With the header, the title
   * card, a pinned CTA and the contact band all fixed, that left a strip of
   * content a hundred-odd points high; under this height the title and the
   * CTA scroll with the page instead. Portrait phones start at 568.
   */
  shortWindowHeight: 520,
} as const;

/**
 * Breakpoints (min-width). The approved artwork has no genuine tablet design —
 * the "tablet" pages are the desktop layout relabelled (gap G-14) — so `md`
 * intentionally promotes tablets to the desktop layout until a tablet design
 * exists.
 */
export const breakpoints = {
  /** Small phones. Base styles target this width first. */
  xs: 0,
  /** Large phones. */
  sm: 400,
  /** Tablets — currently render the desktop layout. */
  md: 768,
  /** Desktop. */
  lg: 1024,
  /** Wide desktop, admin tables. */
  xl: 1280,
} as const;

export type Breakpoint = keyof typeof breakpoints;

export const mediaQuery = (bp: Breakpoint): string =>
  `@media (min-width: ${breakpoints[bp]}px)`;

/** Animation timings, shared so motion feels consistent across surfaces. */
export const motion = {
  duration: { instant: 0, fast: 120, base: 200, slow: 320 },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    decelerate: 'cubic-bezier(0, 0, 0, 1)',
    accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
  },
} as const;

/** Z-index ladder. Keeps stacking order decidable from one file. */
export const zIndex = {
  base: 0,
  sticky: 10,
  header: 20,
  drawer: 30,
  modal: 40,
  toast: 50,
} as const;
